import { ChangeEvent } from "react";
import { FilePDF } from "../../assets/icons/FilePDF";
import UploadFile from "../../assets/icons/UploadFile";
import { Media } from "../../types";
import DefaultButton from "../default-button/DefaultButton";
import { getDateOnly } from "../../misc/utils";
import Eye from "../../assets/icons/Eye";
import { Download } from "../../assets/icons/Download";
import TrashRed from "../../assets/icons/TrashRed";

type FileUploadWithPreviewProps = {
  handleChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  value: File | Media | null;
  additionalClassName?: string;
  wrapperClassName?: string;
  updateValueRaw?: (value: File | null) => void;
  buttonKeys?: string[];
  mode?: string;
  accept?: string;
};

export const FileUploadWithPreview = ({
  handleChange,
  value,
  additionalClassName,
  wrapperClassName = "",
  updateValueRaw,
  mode = "edit",
  buttonKeys = ["view", "download", "delete"],
  accept,
}: FileUploadWithPreviewProps) => {
  if (!value) {
    return (
      <label className={`flex-1 flex flex-row ${wrapperClassName}`}>
        <div
          className={`${mode !== "edit" ? "opacity-50" : ""} flex flex-row gap-2 justify-center items-center flex-1 py-3 rounded-md border border-valley-main text-valley-main cursor-pointer`}
        >
          <UploadFile />
          <span className='text-[15px] font-semibold leading-[18px]'>
            Загрузить
          </span>
        </div>
        <input
          hidden
          type='file'
          accept={accept ? accept : ".pdf, .doc, .docx"}
          disabled={mode !== "edit"}
          onChange={handleChange}
        />
      </label>
    );
  }

  const getName = () => {
    return "name" in value ? value.name : value.filename;
  };

  const getDate = () => {
    return getDateOnly("createdAt" in value ? value.createdAt : new Date());
  };

  const openFile = async () => {
    if ("filename" in value) {
      try {
        const response = await fetch(value.link, { method: "GET" });

        if (!response.ok) {
          throw new Error("Failed to download file");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", value.filename || "");
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Download failed:", error);
        window.open(value.link, "_blank");
      }
    } else {
      const url = URL.createObjectURL(value);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = value.name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    }
  };

  const deleteFile = () => {
    updateValueRaw?.(null);
  };

  const buttonsMeta = [
    {
      action: "view",
      onClick: openFile,
      Icon: Eye,
    },
    {
      action: "download",
      onClick: openFile,
      Icon: Download,
    },
    {
      action: "delete",
      onClick: deleteFile,
      Icon: TrashRed,
    },
  ];

  return (
    <div
      className={`flex-1 flex flex-row p-3 rounded-lg gap-3 border border-valley-background-blue overflow-x-hidden ${wrapperClassName}`}
    >
      <FilePDF />
      <div className='flex flex-col flex-1 overflow-x-hidden'>
        <span className='text-sm font-medium leading-[15.4px] text-valley-black truncate'>
          {getName()}
        </span>
        <span className='mt-1 text-[13px] font-semibold leading-[15.6px] text-valley-gray'>
          {getDate()}
        </span>
      </div>
      <div className='flex flex-row gap-2'>
        {buttonsMeta
          .filter((m) => buttonKeys.includes(m.action))
          .map((m) => {
            const { onClick, Icon } = m;
            return (
              <button
                onClick={onClick}
                className='bg-valley-background-blue p-[6px] rounded-md flex flex-row justify-center items-center'
              >
                <Icon additionalStyle='w-6 h-6' />
              </button>
            );
          })}
      </div>
    </div>
  );
};
